import { vocabularyLemma } from '../lexicon/vocabulary-progress'
import { trainingPageOptions } from './workspace-queries'
import { pageParentColumn } from '../db/page-read'
import { languageSchema } from '../lexicon/lexicon-model'
import { cardReadColumns, cardVisibleTo, learnerCards } from '../db/question-card-triggers'
import { queryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import type { Tel } from '@w/tel'
import type { SpaceResource } from './spaces-resources'
import type { FlashcardsFeRouterCtx } from './flashcards-fe.router-ctx'
import { contentResources, spaceTreeOptions, spaceBacklinksOptions } from '../db/spaces-queries'
import { pageMetaColumns, pageMetaSchema } from '../db/page-read'
import { readableRowsOptions } from '../dead-letter-queue/item-readers'
import type { Query } from '@w/db'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const stringArraySchema = s.array(s.string())
const stringRecordSchema = s.record(s.string(),s.string())
const pageRuleKeysSchema = s.object({ id: s.string(), parentId: s.string().nullable(), ruleKeys: s.transform(s.string(), value => stringArraySchema.parse(JSON.parse(value))) })
const lexiconSummarySchema = s.object({ pageIds: s.transform(s.string(), value => stringArraySchema.parse(JSON.parse(value))), headword: s.string(), senseCount: s.number(), transcription: s.string(), translations: s.transform(s.string(), value => stringRecordSchema.parse(JSON.parse(value))) })
const idNameSchema = s.object({ id: s.string(), name: s.string() })

function pageMetadataOptions(resource: SpaceResource, query: Query) {
  const schema = pageMetaSchema(resource.space.id)
  return { ...queryOptions(query, schema, { staleTime: Infinity }), queryFn: readableRowsOptions(resource, query, schema, 'page', 'PAGE_METADATA').queryFn }
}

/** The open page's own read. Commits, pulls, and maintenance invalidate it; mount reuses the loader result, and the
 * root loader's last-page check reads through it so opening that page decodes its bytes once, not twice. */
export function openPageReadOptions(ctx: FlashcardsFeRouterCtx, params: { spaceId: string; pageId: string }) {
  const pageReadOptions = trainingPageOptions(ctx, { workspaceId: params.spaceId, pageId: params.pageId })
  // Soft deletion removes the tree row, while the open route retains navigation
  // and card review controls until restore or navigation to another page.
  return { ...pageReadOptions, queryFn: (qctx?: { tel?: Tel }) => pageReadOptions.queryFn(qctx).then(rows => rows.filter(page =>
    !(page.title === '' && page.parentId === null && page.content.kind === 'note' && page.content.children.length === 0))) }
}

/**
 * Query ownership follows the physical workspace connection, including replacement.
 * Sign-out retires the session before it removes the stored user (router ctx `auth.signOut`), so a render reads the user.
 */
export function pageViewOptions(ctx: FlashcardsFeRouterCtx, params: { workspaceId: string; spaceId: string; pageId: string }, resource: SpaceResource) {
  return {
    queryKey: ['page-view', resource.userId, params.spaceId, params.pageId, ctx.getUser().settings.lexicon?.nativeLanguage],
    staleTime: Infinity,
    queryFn: (qctx: { tel?: Tel; signal?: AbortSignal }) => loadPageView(ctx, params, resource, qctx.tel ?? ctx.tel, qctx.signal),
  }
}

async function loadPageView(ctx: FlashcardsFeRouterCtx, params: { workspaceId: string; spaceId: string; pageId: string }, resource: SpaceResource, tel: Tel, signal?: AbortSignal) {
  signal?.throwIfAborted()
  const { db, queryClient } = resource
  const stats = resource
  const statsQueryClient = stats.queryClient
  const pageOptions = openPageReadOptions(ctx, params)
  const pagesQueries = contentResources(ctx.spaces.snapshot(resource.space.workspaceId)).map(value => ({ resource: value, options: spaceTreeOptions(value) }))
  const teamspacesOptions = workspaceTeamspacesOptions(resource)
  const workspacesOptions = ctx.rpc.workspace.list.queryOptions()
  const dueCountOptions = dueFlashcardCountOptions(ctx, params)
  const pageFlashcardsOptions = pageFlashcardStatesOptions(ctx, { spaceId: params.spaceId, pageId: params.pageId })
  const backlinksQueries = contentResources(ctx.spaces.snapshot(resource.space.workspaceId)).map(value => ({ resource: value,
    options: spaceBacklinksOptions(value, { spaceId: params.spaceId, pageId: params.pageId }) }))
  resource.translations?.request(params.pageId)
  const { data: pageRows } = await queryClient.load(
    // This required read must fail the view, rather than resolve with undefined.
    { ...pageOptions, throwOnError: true },
    { tel },
  )
  // A retired outer query must not create new child readers after its cache was cleared.
  signal?.throwIfAborted()
  const rulePagesQuery = queryOptions(db.query`SELECT ${db.pages.id}, ${pageParentColumn(db.pages)} AS parentId,
    (SELECT json_group_array(json_extract(parts.fields,'$.key')) FROM crdt_attr_lookup('idx_pages_crdt_attr',NULL,${db.pages}.rowid) parts
      WHERE json_type(parts.fields,'$.qid')='text' AND json_extract(parts.fields,'$.type')='rule') AS ruleKeys FROM ${db.pages}
    WHERE ${db.pages.kind.eq('note')} AND ${db.pages.deletedAt.isNull()}`,
    pageRuleKeysSchema, { staleTime: Infinity })
  // Only lexicon pages read rule keys; for any other kind the read was `WHERE 0 AND ...`, which returns no row.
  const rulePagesOptions: typeof rulePagesQuery = ['lexiconEntry','language','vocabulary'].includes(pageRows[0]?.kind ?? '')
    ? rulePagesQuery
    : { queryKey: ['page-rule-keys', 'none'], staleTime: Infinity, queryFn: () => Promise.resolve([]) }
  const activeKind = pageRows[0]?.content.kind
  const vocabularyPage = activeKind === 'vocabulary'
  const entryPagesOptions = vocabularyPage || activeKind === 'language'
    ? (() => {
        const entries = pageMetadataOptions(resource, db.query`SELECT ${pageMetaColumns(db.pages)} FROM ${db.pages}
          WHERE ${db.pages.kind.eq('lexiconEntry')} AND ${db.pages.deletedAt.isNull()} ORDER BY ${db.pages.id}`)
        return { ...entries, queryFn: (qctx?: { tel?: Tel }) => entries.queryFn(qctx).then(rows => rows.flatMap(row => row.entryId ? [{ pageId: row.id, entryId: row.entryId }] : [])) }
      })()
    : undefined
  const parentLanguageOptions =
    activeKind === 'vocabulary' && pageRows[0]?.parentId
      ? pageMetadataOptions(resource, db.query`
          SELECT ${pageMetaColumns(db.pages)} FROM ${db.pages}
          WHERE ${db.pages.id.eq(pageRows[0].parentId)}
            AND ${db.pages.deletedAt.isNull()}
        `)
      : undefined
  const parentLanguageRows = parentLanguageOptions
    ? (await queryClient.load({ ...parentLanguageOptions }, { tel })).data
    : []
  signal?.throwIfAborted()
  const parentLanguage = activeKind === 'language' ? pageRows[0] : parentLanguageRows[0]
  const parentLanguageCode =
    parentLanguage?.kind === 'language'
      ? languageSchema.parse('language' in parentLanguage ? parentLanguage.language : parentLanguage.content.kind === 'language' ? parentLanguage.content.language : undefined)
      : undefined
  const frequencyListOptions = parentLanguageCode
    ? {
        queryKey: ['frequency-list', parentLanguageCode],
        queryFn: () => ctx.frequencyList(parentLanguageCode),
        staleTime: Infinity,
      }
    : undefined
  const frequencyList = frequencyListOptions
    ? (await queryClient.load({ ...frequencyListOptions }, { tel })).data
    : []
  signal?.throwIfAborted()
  const hasFrequencyList = frequencyList.length > 0
  const languageChildrenOptions =
    (vocabularyPage || activeKind === 'language') && parentLanguage
      ? pageMetadataOptions(resource, db.query`
        SELECT ${pageMetaColumns(db.pages)} FROM ${db.pages}
        WHERE ${db.pages.deletedAt.isNull()}
          AND ${pageParentColumn(db.pages)} IN (
            SELECT value FROM json_each(${JSON.stringify([parentLanguage.id, params.pageId])})
            UNION SELECT ${db.pages.id} FROM ${db.pages}
              WHERE ${db.pages.kind.eq('vocabulary')} AND ${pageParentColumn(db.pages)} = ${parentLanguage.id}
          )
        ORDER BY ${db.pages.title}, ${db.pages.id}
      `)
      : undefined
  const adoptedLemmaOptions =
    activeKind === 'vocabulary' && hasFrequencyList && parentLanguageCode
      ? (() => {
          const a = db.pages
          // The frequency list defines the displayed vocabulary and its tab totals.
          // Count live sense maps from postings; totals never open entry documents.
          const lemma = db.query`CASE WHEN substr(${a.headwordKey},1,4) IN ('der ','die ','das ')
            THEN trim(substr(${a.headwordKey},5)) ELSE ${a.headwordKey} END`
          const displayedIds = db.query`SELECT ${a.id} FROM ${a}
            WHERE ${a.language.eq(parentLanguageCode)} AND ${a.kind.eq('lexiconEntry')} AND ${a.deletedAt.isNull()}
              AND ${lemma} IN (SELECT value FROM json_each(${JSON.stringify(frequencyList.map(row => vocabularyLemma(row.word)))}))`
          return queryOptions(db.query`
            WITH summaries AS MATERIALIZED (
              SELECT ${a.id}, ${lemma} AS headword, COALESCE(${a.transcription},'') AS transcription, COALESCE(${a.translations},'{}') AS labels,
                (SELECT COUNT(DISTINCT senses.node) FROM crdt_attr_lookup('idx_pages_crdt_attr',NULL,${a}.rowid,'cefr') senses
                  WHERE json_extract(senses.fields,'$.cefr') IN ('A1','A2','B1','B2','C1','C2')
                    AND json_type(senses.fields,'$.id')='text' AND json_type(senses.fields,'$.gloss')='text') AS pooledSenses
              FROM ${a} WHERE ${a.id} IN (${displayedIds})
              ORDER BY ${a.id}
            )
            SELECT headword,
              SUM(pooledSenses) AS senseCount,
              MIN(transcription) AS transcription, json_group_array(id) AS pageIds,
              (SELECT json_group_object(label.key,label.value) FROM summaries AS translated, json_each(translated.labels) AS label
                WHERE translated.headword=summary.headword ORDER BY translated.id) AS translations
            FROM summaries AS summary GROUP BY headword ORDER BY headword`,
            lexiconSummarySchema, { staleTime: Infinity })
        })()
      : undefined
  await Promise.all([
    queryClient.load(rulePagesOptions, { tel }),
    ...pagesQueries.map(value => value.resource.queryClient.load(value.options, { tel })),
    ctx.queryClient.load({ ...workspacesOptions }, { tel }),
    // A denied reader unmounts its permission observer. Reopening readable
    // content after rejoin must refresh that invalidated terminal denial.
    pageRows[0] ? _loadOrRevalidate(ctx.queryClient, { ...ctx.rpc.page.levels.queryOptions({ workspaceId: resource.space.workspaceId }), throwOnError: false }, tel) : Promise.resolve(),
    queryClient.load(teamspacesOptions, { tel }),
    statsQueryClient.load({ ...dueCountOptions }, { tel }),
    statsQueryClient.load({ ...pageFlashcardsOptions }, { tel }),
    ...backlinksQueries.map(value => value.resource.queryClient.load(value.options, { tel })),
    entryPagesOptions
      ? queryClient.load({ ...entryPagesOptions }, { tel })
      : Promise.resolve(),
    languageChildrenOptions
      ? queryClient.load({ ...languageChildrenOptions }, { tel })
      : Promise.resolve(),
    adoptedLemmaOptions
      ? queryClient.load({ ...adoptedLemmaOptions }, { tel })
      : Promise.resolve(),
  ])
  return {
    pageOptions,
    rulePagesOptions,
    pagesQueries,
    queryClient,
    statsQueryClient,
    workspacesOptions,
    teamspacesOptions,
    dueCountOptions,
    pageFlashcardsOptions,
    backlinksQueries,
    entryPagesOptions,
    languageChildrenOptions,
    frequencyListOptions,
    adoptedLemmaOptions,
    languagePageId: parentLanguage?.id,
    parentLanguageCode,
  }
}

export function dueFlashcardCountOptions(
  ctx: FlashcardsFeRouterCtx,
  params: { workspaceId: string },
) {
  const db = ctx.spaces.get(params.workspaceId).db
  const user = ctx.getUser()
  const countSchema = s.object({ count: s.number().int() })
  const options = queryOptions(db.query`
    SELECT COUNT(*) AS count FROM ${learnerCards(user.id)}
    WHERE ${cardVisibleTo(db.flashcards, user.id)}
      AND ${db.flashcards.deletedAt.isNull()} AND ${db.flashcards.graduated.eq(false)}
      
      AND ${db.flashcards.frozenAt.isNull()}
      AND ${db.flashcards.deletedAt.isNull()}
      AND ${db.flashcards.repeatAt.isNotNull()}
  `, countSchema)
  return {
    ...options,
    queryFn: () => {
      const now = new Date(Date.now())
      return db.query`
        SELECT COUNT(*) AS count FROM ${learnerCards(user.id)}
        WHERE ${cardVisibleTo(db.flashcards, user.id)}
          AND ${db.flashcards.deletedAt.isNull()} AND ${db.flashcards.graduated.eq(false)}
          
      AND ${db.flashcards.frozenAt.isNull()}
          AND ${db.flashcards.deletedAt.isNull()}
          AND (${db.flashcards.repeatAt.eq('now')} OR ${db.flashcards.repeatAt.lte(now)})
      `.execute(countSchema)
    },
  }
}

function pageFlashcardStatesOptions(
  ctx: FlashcardsFeRouterCtx,
  params: { spaceId: string; pageId: string },
) {
  const db = ctx.spaces.get(params.spaceId).db
  const user = ctx.getUser()
  const query = db.query`
    SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(user.id)}
    WHERE ${cardVisibleTo(db.flashcards, user.id)}
      AND ${db.flashcards.pageId.eq(params.pageId)}
  `
  const resource = ctx.spaces.get(params.spaceId)
  return { ...queryOptions(query, db.flashcards.__selectSchema, { staleTime: Infinity }), queryFn: readableRowsOptions(resource, query, db.flashcards.__selectSchema, 'card', 'CARD_DATA').queryFn }
}

export function workspaceTeamspacesOptions(resource: SpaceResource) {
  const { db } = resource
  return queryOptions(db.query`SELECT ${db.pages.id} AS id, ${db.pages.title} AS name FROM ${db.pages}
    WHERE ${db.pages.kind} != 'personalRoot' AND NOT EXISTS (SELECT 1 FROM pageRelations r WHERE r.toPageId=${db.pages.id} AND r.type='child') AND ${db.pages.deletedAt.isNull()}
      AND COALESCE(${db.pages.archived},0)=0 ORDER BY ${db.pages.title}, ${db.pages.id}`,
    idNameSchema, { staleTime: Infinity })
}

/** A successful permission answer the page already holds opens the route at once and refreshes behind it
 * (stale-while-revalidate, as TanStack Router's `ensureQueryData({ revalidateIfStale: true })`). Awaited instead: no
 * answer yet; a failed answer (status 'error', such as the 403 a denied reader keeps until rejoin); and a successful
 * answer explicitly invalidated by a permission or membership change. */
function _loadOrRevalidate(queryClient: FlashcardsFeRouterCtx['queryClient'], options: Parameters<FlashcardsFeRouterCtx['queryClient']['load']>[0], tel: Tel | undefined) {
  const cached = queryClient.getQuery(options.queryKey)?.state
  if (cached?.status !== 'success' || cached.isInvalidated) return queryClient.load(options, { tel })
  queryClient.prefetchQuery(options, { tel })
  return Promise.resolve()
}
